import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const STATUS_TABS = ["application_incomplete", "applied", "under_review", "approved", "changes_requested", "suspended", "rejected"];

export default function AdminPartners() {
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [status, setStatus] = useState("applied");
  const [selected, setSelected] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [newKw, setNewKw] = useState("");
  const [pendingCourses, setPendingCourses] = useState([]);

  const loadStats = () => api.get("/api/admin/partners/stats").then(({ data }) => setStats(data));
  const loadList = (s) => api.get(`/api/admin/partners?status=${s}`).then(({ data }) => setPartners(data));
  const loadKws = () => api.get("/api/admin/compliance/keywords").then(({ data }) => setKeywords(data));
  const loadPending = async () => {
    // We list flagged partner courses by iterating all partners' courses server-side
    // For simplicity, use client-side aggregation via /api/admin/partners
    const { data: all } = await api.get("/api/admin/partners");
    const pending = [];
    for (const p of all) {
      const { data: det } = await api.get(`/api/admin/partners/${p.id}`);
      for (const c of det.courses || []) {
        if (c.compliance_status === "pending_review") {
          pending.push({ ...c, partner_name: p.organization_name, partner_id: p.id });
        }
      }
    }
    setPendingCourses(pending);
  };

  useEffect(() => { loadStats(); loadKws(); loadPending(); }, []);
  useEffect(() => { loadList(status); }, [status]);

  const openPartner = async (p) => {
    const { data } = await api.get(`/api/admin/partners/${p.id}`);
    setSelected(data);
  };

  const act = async (endpoint, body) => {
    if (!selected) return;
    try {
      await api.post(`/api/admin/partners/${selected.id}/${endpoint}`, body || {});
      await Promise.all([loadStats(), loadList(status), openPartner({ id: selected.id })]);
      toast.success(`Partner ${endpoint}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const addKw = async () => {
    if (!newKw.trim()) return;
    try {
      await api.post("/api/admin/compliance/keywords", { keyword: newKw.trim() });
      setNewKw("");
      await loadKws();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const delKw = async (kw) => {
    if (!window.confirm(`Delete keyword "${kw}"?`)) return;
    await api.delete(`/api/admin/compliance/keywords/${encodeURIComponent(kw)}`);
    await loadKws();
  };

  const approveCourse = async (courseId) => {
    await api.post(`/api/admin/partners/courses/${courseId}/compliance-approve`);
    await Promise.all([loadStats(), loadPending()]);
    toast.success("Course approved for compliance");
  };

  const rejectCourse = async (courseId) => {
    const reason = window.prompt("Rejection reason (visible to partner)?", "");
    if (reason === null) return;
    await api.post(`/api/admin/partners/courses/${courseId}/compliance-reject`, { reason });
    await Promise.all([loadStats(), loadPending()]);
  };

  return (
    <div className="space-y-6" data-testid="admin-partners-root">
      <h1 className="text-2xl font-bold">Training Partners</h1>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {STATUS_TABS.map((s) => (
            <Card key={s} data-testid={`admin-partners-stat-${s}`}>
              <CardContent className="p-4">
                <div className="text-xs uppercase text-gray-500">{s.replace("_", " ")}</div>
                <div className="text-2xl font-bold">{stats.partners[s] || 0}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="partners">
        <TabsList>
          <TabsTrigger value="partners" data-testid="admin-partners-tab-list">Partners</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="admin-partners-tab-compliance">
            Compliance ({pendingCourses.length})
          </TabsTrigger>
          <TabsTrigger value="keywords" data-testid="admin-partners-tab-keywords">
            Keywords ({keywords.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="partners" className="space-y-4 mt-4">
          <div className="flex gap-2 flex-wrap">
            {STATUS_TABS.map((s) => (
              <button key={s}
                data-testid={`admin-partners-filter-${s}`}
                onClick={() => setStatus(s)}
                className={`px-3 py-1 rounded-full text-xs ${
                  status === s ? "bg-[#064E3B] text-white" : "bg-gray-200 text-gray-700"
                }`}>
                {s.replace("_", " ")} ({stats?.partners?.[s] || 0})
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-1 space-y-2">
              {partners.length === 0 ? (
                <div className="text-sm text-gray-500 p-4" data-testid="admin-partners-empty">
                  No partners in this status.
                </div>
              ) : partners.map((p) => (
                <Card key={p.id}
                  data-testid="admin-partners-row"
                  className={`cursor-pointer ${selected?.id === p.id ? "ring-2 ring-[#DC2626]" : ""}`}
                  onClick={() => openPartner(p)}>
                  <CardContent className="p-3">
                    <div className="font-semibold text-sm truncate">{p.organization_name}</div>
                    <div className="text-xs text-gray-500 truncate">{p.contact_email}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(p.created_at).toLocaleDateString()}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="md:col-span-2">
              {selected ? (
                <Card data-testid="admin-partners-detail-panel">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{selected.organization_name}</CardTitle>
                        <div className="text-sm text-gray-500 mt-1">{selected.contact_email}</div>
                      </div>
                      <Badge className="capitalize">{selected.status}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div><b>Contact:</b> {selected.contact_name}</div>
                    <div><b>Phone:</b> {selected.contact_phone || "—"}</div>
                    <div><b>Website:</b> {selected.website || "—"}</div>
                    <div><b>Country/Province:</b> {selected.country} / {selected.province || "—"}</div>
                    <div><b>About:</b> {selected.about || "—"}</div>
                    <div><b>Accreditations:</b> {(selected.accreditations || []).join(", ") || "—"}</div>
                    <div><b>Media quota:</b> {selected.media_used_mb} MB / {selected.media_quota_mb} MB</div>
                    <div><b>Revenue share (informational):</b> {selected.revenue_share_pct}%</div>
                    <div><b>Courses:</b> {(selected.courses || []).length}</div>

                    <div className="flex flex-wrap gap-2 pt-3 border-t">
                      {selected.status !== "approved" && (
                        <Button size="sm" onClick={() => act("approve")}
                          data-testid="admin-partner-approve-btn"
                          className="bg-emerald-600 hover:bg-emerald-700">
                          Approve
                        </Button>
                      )}
                      {selected.status !== "under_review" && (
                        <Button size="sm" variant="outline"
                          data-testid="admin-partner-underreview-btn"
                          onClick={() => act("under-review")}>Mark under review</Button>
                      )}
                      {selected.status !== "suspended" && (
                        <Button size="sm" variant="outline"
                          data-testid="admin-partner-suspend-btn"
                          onClick={() => act("suspend")}>Suspend</Button>
                      )}
                      {selected.status !== "rejected" && (
                        <Button size="sm" variant="outline"
                          data-testid="admin-partner-reject-btn"
                          onClick={() => {
                            const reason = window.prompt("Rejection reason?", "");
                            if (reason !== null) act("reject", { reason });
                          }} className="text-red-600 border-red-200">
                          Reject
                        </Button>
                      )}
                      <Button size="sm" variant="outline"
                        data-testid="admin-partner-request-changes-btn"
                        onClick={() => {
                          const note = window.prompt("What changes should the partner make? (visible to them)", "");
                          if (note && note.trim()) act("request-changes", { note });
                        }}
                        className="text-amber-700 border-amber-200">
                        Request changes
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card><CardContent className="p-8 text-center text-gray-500"
                  data-testid="admin-partners-detail-empty">
                  Select a partner to view details.
                </CardContent></Card>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="compliance" className="space-y-3 mt-4">
          {pendingCourses.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-gray-500"
              data-testid="admin-compliance-empty">
              No courses awaiting compliance review.
            </CardContent></Card>
          ) : pendingCourses.map((c) => (
            <Card key={c.id} data-testid="admin-compliance-row">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1">
                  <div className="font-semibold">{c.title}</div>
                  <div className="text-xs text-gray-500">
                    {c.partner_name} · /{c.slug}
                  </div>
                  {c.compliance_flags?.length > 0 && (
                    <div className="text-xs text-amber-800 mt-1">
                      Flags: {c.compliance_flags.join(", ")}
                    </div>
                  )}
                </div>
                <Button size="sm" onClick={() => approveCourse(c.id)}
                  data-testid="admin-compliance-approve-btn"
                  className="bg-emerald-600 hover:bg-emerald-700">Approve</Button>
                <Button size="sm" variant="outline"
                  data-testid="admin-compliance-reject-btn"
                  onClick={() => rejectCourse(c.id)}
                  className="text-red-600 border-red-200">Reject</Button>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="keywords" className="space-y-3 mt-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex gap-2">
                <Input placeholder="Add a keyword (e.g. surgery)"
                  data-testid="admin-kw-input"
                  value={newKw} onChange={(e) => setNewKw(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addKw()} />
                <Button onClick={addKw} data-testid="admin-kw-add-btn">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {keywords.map((k) => (
                  <button key={k.keyword}
                    data-testid="admin-kw-chip"
                    className="text-xs bg-gray-100 hover:bg-red-100 px-2 py-1 rounded-full"
                    onClick={() => delKw(k.keyword)}
                    title="Click to delete">
                    {k.keyword} ✕
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Any partner course whose title/description matches one of these keywords is auto-flagged for review.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
